import { useState, useEffect } from 'react';
import { api, uploadUrl } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import ImageManager from '../components/ImageManager';
import { scanTextOCR, isIOS } from '../native';
import { parseAllergens } from '../utils/allergenParser';
import AllergenSection from '../components/AllergenSection';
import { useTranslation } from 'react-i18next';

const UNITS = ['stk','g','kg','ml','l','pk','bk','pose','bakke','flaske','dåse'];
const EMPTY_FORM = { name: '', brand: '', barcode: '', category: 'freezer', unit: 'stk', weight_per_unit: '', days_after_opening: '', allergens: [], traces: [], labels: [], ingredients_text: '', staple_buy_qty: 1 };

export default function CatalogPage() {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [kbHeight, setKbHeight] = useState(0);
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    if (!isIOS()) return;
    let l1, l2, l3, l4;
    import(/* @vite-ignore */ '@capacitor/keyboard').then(({ Keyboard }) => {
      Keyboard.addListener('keyboardWillShow', info => setKbHeight(info.keyboardHeight)).then(l => { l1 = l; });
      Keyboard.addListener('keyboardDidShow',  info => setKbHeight(info.keyboardHeight)).then(l => { l3 = l; });
      Keyboard.addListener('keyboardWillHide', () => setKbHeight(0)).then(l => { l2 = l; });
      Keyboard.addListener('keyboardDidHide',  () => setKbHeight(0)).then(l => { l4 = l; });
    }).catch(() => {});
    return () => { l1?.remove(); l2?.remove(); l3?.remove(); l4?.remove(); };
  }, []);

  const runOCR = async (setForm) => {
    setScanning(true);
    try {
      const text = await scanTextOCR();
      if (!text) return;
      const { allergens, labels } = parseAllergens(text);
      setForm(prev => ({
        ...prev,
        allergens: [...new Set([...(prev.allergens || []), ...allergens])],
        labels:    [...new Set([...(prev.labels    || []), ...labels])],
        ingredients_text: prev.ingredients_text || text,
      }));
    } finally { setScanning(false); }
  };

  useEffect(() => {
    api.getCatalog(filter === 'all' ? null : filter).then(setCatalog);
  }, [filter]);

  const reload = () => api.getCatalog(filter === 'all' ? null : filter).then(setCatalog);

  const filtered = catalog
    .filter(p => !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.brand || '').toLowerCase().includes(search.toLowerCase())
    );

  const openEdit = (p) => { setEditForm({ ...p }); setSelected(p); };

  const saveEdit = async () => {
    await api.updateProduct(editForm.id, { ...editForm, is_staple: editForm.is_staple || false, staple_threshold: editForm.staple_threshold || 0, staple_buy_qty: editForm.staple_buy_qty || 1 });
    await reload();
    setSelected(null); setEditForm(null);
  };

  const deleteProduct = async (p) => {
    const ok = await confirm(t('catalog.deleteConfirm', { name: p.name }));
    if (!ok) return;
    await api.deleteProduct(p.id, true);
    setCatalog(prev => prev.filter(x => x.id !== p.id));
    setSelected(null); setEditForm(null);
  };



  const createProduct = async () => {
    if (!createForm.name.trim()) return;
    setCreating(true);
    try {
      await api.createProduct({
        ...createForm,
        weight_per_unit: createForm.weight_per_unit ? parseFloat(createForm.weight_per_unit) : null,
        days_after_opening: createForm.days_after_opening ? parseInt(createForm.days_after_opening) : null,
      });
      await reload();
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
    } finally { setCreating(false); }
  };

  const CAT_ICON = { freezer: '🧊', fridge: '🥛', pantry: '🥫' };
  const ef = (k, v) => setEditForm(p => ({ ...p, [k]: v }));
  const cf = (k, v) => setCreateForm(p => ({ ...p, [k]: v }));

  const scrollToInput = (e) =>
    setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "var(--bg-base)" }}>
      {ConfirmDialog}

      <div className="pt-safe" style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-card)", flexShrink: 0 }}>
      <div style={{ padding: "14px 16px 12px", position: 'relative' }}>
        <div style={{ paddingRight: 60, marginBottom: 12 }}>
          <h1 className="ft-screen-title">📦 {t('catalog.title')}</h1>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="ft-fab no-select"
          style={{ background: 'var(--brand)', boxShadow: '0 4px 16px color-mix(in srgb, var(--brand) 40%, transparent)' }}>+</button>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('catalog.searchPlaceholder')}
          className="input-field" style={{ marginBottom: 8, fontSize: 14 }} />
        <div className="flex gap-2">
          {[['all', t('common.all')],['freezer','🧊'],['fridge','🥛'],['pantry','🥫']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                ft-chip no-select ${filter === v ? 'active' : ''}`} style={filter === v ? { background: 'var(--brand)', color: '#fff' } : {}}>{l}</button>
          ))}
        </div>
      </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-safe">
        {filtered.length === 0
          ? <div className="flex flex-col items-center py-16 text-slate-500">
              <p>{t('catalog.noProducts')}</p>
              <button onClick={() => setShowCreate(true)} className="mt-3 text-blue-400 underline text-sm">{t('catalog.addFirst')}</button>
            </div>
          : <div style={{ background: "var(--bg-card)" }}>{filtered.map(p => (
            <button key={p.id} onClick={() => openEdit(p)}
              className="ft-list-item" style={{ width: "100%", textAlign: "left" }}>
              <div className="ft-thumb">
                {p.thumbnail || p.images?.[0]?.filename
                  ? <img src={p.images?.[0] ? uploadUrl(p.images[0].filename) : p.thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  : <span className="w-full h-full flex items-center justify-center text-2xl">{CAT_ICON[p.category]}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="ft-item-name">{p.name}</p>
                <p className="ft-item-meta">
                  {[p.brand, p.weight_per_unit && ['g','kg','ml','l','cl','dl'].includes(p.unit) ? `${p.weight_per_unit}${p.unit}` : null].filter(Boolean).join(' · ')}
                  {p.is_staple && <span style={{ marginLeft: 4 }}>🛒</span>}
                </p>
              </div>
              <div className="ft-qty" style={{ flexShrink: 0, minWidth: 52, textAlign: 'right' }}>
                {p.in_stock > 0 ? (<>
                  <div>
                    <span className="text-2xl font-bold text-white">{p.in_stock}</span>
                    <span className="text-xs text-slate-500 ml-1">{['g','kg','ml','l','cl','dl'].includes(p.unit) ? 'stk' : t('units.' + (p.unit || 'stk'))}</span>
                  </div>
                  {p.weight_per_unit && ['g','kg','ml','l','cl','dl'].includes(p.unit) && (
                    <div className="text-xs text-slate-400">
                      {p.in_stock * p.weight_per_unit >= 1000 && ['g','ml'].includes(p.unit)
                        ? `${((p.in_stock * p.weight_per_unit) / 1000).toFixed(1).replace(/\.0$/, '')} ${ p.unit === 'g' ? 'kg' : 'l'}`
                        : `${p.in_stock * p.weight_per_unit} ${p.unit}`}
                    </div>
                  )}
                </>) : (
                  <div style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{t('catalog.outOfStock')}</div>
                )}
              </div>
            </button>
          ))}</div>
        }
      </div>

      {/* CREATE PRODUCT MODAL */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end" onClick={() => setShowCreate(false)}>
          <div style={{ width: "100%", maxHeight: isIOS() && kbHeight ? `calc(100vh - ${kbHeight}px - 60px)` : "90vh", marginBottom: isIOS() ? kbHeight : 0, background: "var(--bg-elevated)", borderRadius: "20px 20px 0 0", overflowY: "auto", overflowX: "hidden", paddingBottom: "calc(12rem + env(safe-area-inset-bottom))" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 8px" }}><div className="ft-sheet-handle" /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
              <h2 className="font-bold text-lg">{t('catalog.newProduct')}</h2>
              <button onClick={() => setShowCreate(false)} className="text-2xl text-slate-400 w-10 h-10 flex items-center justify-center">×</button>
            </div>
            <div className="p-4 space-y-3">
              <Field label={t('catalog.nameLabel')}>
                <input value={createForm.name} onChange={e => cf('name', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.namePlaceholder')} autoFocus />
              </Field>
              <Field label={t('catalog.brandLabel')}>
                <input value={createForm.brand} onChange={e => cf('brand', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.brandPlaceholder')} />
              </Field>
              <Field label={t('catalog.barcodeLabel')}>
                <input value={createForm.barcode} onChange={e => cf('barcode', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.barcodePlaceholder')} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('catalog.categoryLabel')}>
                  <select value={createForm.category} onChange={e => cf('category', e.target.value)} className="input-field">
                    <option value="freezer">🧊 {t('nav.freezer')}</option>
                    <option value="fridge">🥛 {t('nav.fridge')}</option>
                    <option value="pantry">🥫 {t('nav.pantry')}</option>
                  </select>
                </Field>
                <Field label={t('catalog.unitLabel')}>
                  <select value={createForm.unit} onChange={e => cf('unit', e.target.value)} className="input-field">
                    {UNITS.map(u => <option key={u} value={u}>{t('units.' + u)}</option>)}
                  </select>
                </Field>
              </div>
              <Field label={t('catalog.sizeLabel')}>
                <input type="number" value={createForm.weight_per_unit} onChange={e => cf('weight_per_unit', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.sizePlaceholder')} />
              </Field>
              <Field label={t('catalog.daysLabel')}>
                <input type="number" value={createForm.days_after_opening} onChange={e => cf('days_after_opening', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.daysPlaceholder')} />
              </Field>
              <AllergenSection
                allergens={createForm.allergens} labels={createForm.labels}
                ingredients_text={createForm.ingredients_text}
                onAllergenToggle={key => cf('allergens', createForm.allergens.includes(key) ? createForm.allergens.filter(a => a !== key) : [...createForm.allergens, key])}
                onLabelToggle={key => cf('labels', createForm.labels.includes(key) ? createForm.labels.filter(l => l !== key) : [...createForm.labels, key])}
                onIngredientsChange={v => cf('ingredients_text', v)}
                onOCR={() => runOCR(setCreateForm)} scanning={scanning} />
              <button onClick={createProduct} disabled={!createForm.name.trim() || creating}
                className="ft-btn-primary" style={{ marginTop: 8 }}>
                {creating ? t('catalog.creating') : t('catalog.createButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT PRODUCT MODAL */}
      {selected && editForm && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end" onClick={() => { setSelected(null); setEditForm(null); }}>
          <div style={{ width: "100%", maxHeight: isIOS() && kbHeight ? `calc(100vh - ${kbHeight}px - 60px)` : "90vh", marginBottom: isIOS() ? kbHeight : 0, background: "var(--bg-elevated)", borderRadius: "20px 20px 0 0", overflowY: "auto", overflowX: "hidden", paddingBottom: "calc(12rem + env(safe-area-inset-bottom))" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 8px" }}><div className="ft-sheet-handle" /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
              <h2 className="font-bold text-lg">{t('catalog.editProduct')}</h2>
              <button onClick={() => { setSelected(null); setEditForm(null); }} className="text-2xl text-slate-400 w-10 h-10 flex items-center justify-center">×</button>
            </div>
            <div className="p-4 space-y-3">
              {[[t('catalog.nameLabel'), 'name'],[t('catalog.brandLabel'), 'brand'],[t('catalog.barcodeLabel'), 'barcode']].map(([label, key]) => (
                <div key={key} className="space-y-1">
                  <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{label}</label>
                  <input value={editForm[key] || ''} onChange={e => ef(key, e.target.value)}
                    onFocus={scrollToInput} className="input-field" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{t('catalog.categoryLabel')}</label>
                  <select value={editForm.category} onChange={e => ef('category', e.target.value)} className="input-field">
                    <option value="freezer">🧊 {t('nav.freezer')}</option>
                    <option value="fridge">🥛 {t('nav.fridge')}</option>
                    <option value="pantry">🥫 {t('nav.pantry')}</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{t('catalog.unitLabel')}</label>
                  <select value={editForm.unit || 'stk'} onChange={e => ef('unit', e.target.value)} className="input-field">
                    {UNITS.map(u => <option key={u} value={u}>{t('units.' + u)}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{t('catalog.sizeLabel')}</label>
                <input type="number" value={editForm.weight_per_unit || ''} onChange={e => ef('weight_per_unit', e.target.value)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.sizePlaceholder')} />
              </div>
              <div className="space-y-1">
                <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{t('catalog.daysLabel')}</label>
                <input type="number" value={editForm.days_after_opening || ''} onChange={e => ef('days_after_opening', e.target.value ? parseInt(e.target.value) : null)}
                  onFocus={scrollToInput} className="input-field" placeholder={t('catalog.daysPlaceholder')} />
              </div>

              {/* Staple-vare indstillinger */}
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editForm.is_staple ? 12 : 0 }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>
                      {t('catalog.staple')}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>
                      {t('catalog.stapleDesc')}
                    </p>
                  </div>
                  <div onClick={() => ef('is_staple', !editForm.is_staple)} style={{
                    width: 46, height: 28, borderRadius: 999, cursor: 'pointer',
                    background: editForm.is_staple ? 'var(--brand)' : 'var(--fg-4)',
                    position: 'relative', transition: 'background 200ms', flexShrink: 0,
                  }}>
                    <div style={{
                      width: 22, height: 22, background: 'white', borderRadius: '50%',
                      position: 'absolute', top: 3,
                      left: editForm.is_staple ? 21 : 3,
                      transition: 'left 200ms var(--ease-spring)',
                    }} />
                  </div>
                </div>
                {editForm.is_staple && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>
                        {t('catalog.stapleThreshold')}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <button type="button"
                          onClick={() => ef('staple_threshold', Math.max(0, (editForm.staple_threshold || 0) - 1))}
                          style={{ width: 36, height: 36, borderRadius: 8, border: 'none', background: 'var(--bg-card)', color: 'var(--fg-1)', fontSize: 20, cursor: 'pointer' }}>−</button>
                        <div style={{ flex: 1, textAlign: 'center', fontSize: 20, fontWeight: 600, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>
                          {editForm.staple_threshold || 0}
                          <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--fg-3)', marginLeft: 4 }}>
                            {editForm.staple_threshold === 0 ? t('catalog.thresholdWhenEmpty') : 'stk'}
                          </span>
                        </div>
                        <button type="button"
                          onClick={() => ef('staple_threshold', (editForm.staple_threshold || 0) + 1)}
                          style={{ width: 36, height: 36, borderRadius: 8, border: 'none', background: 'var(--bg-card)', color: 'var(--fg-1)', fontSize: 20, cursor: 'pointer' }}>+</button>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>
                        {t('catalog.addToShopping')}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <button type="button"
                          onClick={() => ef('staple_buy_qty', Math.max(1, (editForm.staple_buy_qty || 1) - 1))}
                          style={{ width: 36, height: 36, borderRadius: 8, border: 'none', background: 'var(--bg-card)', color: 'var(--fg-1)', fontSize: 20, cursor: 'pointer' }}>−</button>
                        <div style={{ flex: 1, textAlign: 'center', fontSize: 20, fontWeight: 600, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>
                          {editForm.staple_buy_qty || 1}
                          <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--fg-3)', marginLeft: 4 }}>
                            {editForm.weight_per_unit && ['g','kg','ml','l','cl','dl'].includes(editForm.unit)
                              ? (() => {
                                  const total = (editForm.staple_buy_qty || 1) * parseFloat(editForm.weight_per_unit);
                                  if ((editForm.unit === 'g' || editForm.unit === 'ml') && total >= 1000)
                                    return `stk (${parseFloat((total/1000).toFixed(2))} ${editForm.unit === 'g' ? 'kg' : 'l'})`;
                                  return `stk (${total} ${editForm.unit})`;
                                })()
                              : 'stk'}
                          </span>
                        </div>
                        <button type="button"
                          onClick={() => ef('staple_buy_qty', (editForm.staple_buy_qty || 1) + 1)}
                          style={{ width: 36, height: 36, borderRadius: 8, border: 'none', background: 'var(--bg-card)', color: 'var(--fg-1)', fontSize: 20, cursor: 'pointer' }}>+</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <AllergenSection
                allergens={editForm.allergens || []} labels={editForm.labels || []}
                ingredients_text={editForm.ingredients_text}
                onAllergenToggle={key => ef('allergens', (editForm.allergens||[]).includes(key) ? (editForm.allergens||[]).filter(a => a !== key) : [...(editForm.allergens||[]), key])}
                onLabelToggle={key => ef('labels', (editForm.labels||[]).includes(key) ? (editForm.labels||[]).filter(l => l !== key) : [...(editForm.labels||[]), key])}
                onIngredientsChange={v => ef('ingredients_text', v)}
                onOCR={() => runOCR(setEditForm)} scanning={scanning} />

              <div>
                <label style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 8 }}>{t('catalog.imagesLabel')}</label>
                {/* Show Open Food Facts thumbnail if present and no custom images */}
                {editForm.thumbnail && (editForm.images || []).length === 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{t('catalog.offThumbnail')}</p>
                    <div className="flex items-center gap-3">
                      <img src={editForm.thumbnail} alt="" className="w-20 h-20 rounded-xl object-cover " style={{ background: "var(--bg-elevated)" }} />
                      <div className="space-y-1.5 flex-1">
                        <p style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "DM Sans, sans-serif", display: "block", marginBottom: 4 }}>{t('catalog.replaceImage')}</p>
                        <button
                          onClick={() => { setEditForm(f => ({ ...f, thumbnail: '' })); ef('thumbnail', ''); }}
                          className="text-xs text-red-400 border border-red-800 rounded-lg px-3 py-1.5 active:bg-red-900/30">
                          {t('catalog.removeImage')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <ImageManager
                  productId={editForm.id}
                  images={editForm.images || []}
                  onImagesChange={evt => {
                    if (evt.type === 'uploaded') {
                      const updated = [...(editForm.images || []), evt.image];
                      setEditForm(f => ({ ...f, images: updated }));
                      setCatalog(prev => prev.map(p => p.id === editForm.id ? { ...p, images: updated } : p));
                    }
                    if (evt.type === 'delete') {
                      const updated = (editForm.images || []).filter(i => i.id !== evt.id);
                      setEditForm(f => ({ ...f, images: updated }));
                      setCatalog(prev => prev.map(p => p.id === editForm.id ? { ...p, images: updated } : p));
                    }
                  }}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => deleteProduct(selected)}
                  className="flex-1 py-3 border border-red-700 rounded-xl text-red-400 text-sm active:bg-red-900/30">{t('catalog.deleteProduct')}</button>
                <button onClick={saveEdit}
                  className="ft-btn-primary" style={{ flex: 1 }}>{t('common.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><label className="text-sm font-medium text-slate-400">{label}</label>{children}</div>;
}
